import React, { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import { CircularProgress, Tooltip } from "@mui/material";
import SyncAltOutlinedIcon from "@mui/icons-material/SyncAltOutlined";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSelector } from "react-redux";
import { DataContext } from "./index";
import { useTradingColors } from "./tradingColorConfig";
import {
  showToast,
  WEBSERVICE,
  TRADESERVICE,
  CACHESERVICE,
  handleGetAssetCode,
  handleGetAssetId,
  checkCurrencyPairAccreditedStatus,
} from "../../CoreDataService";
import AccreditationVerificationPopup from "../Accredited-Kyc/AccreditationVerificationPopup";
import TermsAgreementPopup from "../Accredited-Kyc/TermsAgreementPopup";
import FeeModal from "../Convert/FeeModal";

const QuickSwap = () => {
  const {
    currencycode = "BTC",
    basecurrency = "USDT",
    refresh,
    setRefresh,
    updateBalance,
  } = useContext(DataContext);

  const theme = localStorage.getItem("theme");
  const { btncolor } = useSelector((state) => state);
  const { upColor = "#16a34a", downColor = "#ef4444" } = useTradingColors();

  // Tab & Type: Buy (actionType: 1) or Sell (actionType: 2)
  const [activeTab, setActiveTab] = useState(0);
  const [buttonType, setButtonType] = useState("Buy");
  const [buttonTypeSmall, setButtonTypeSmall] = useState("buy");
  const [accountType, setAccountType] = useState("Buying");
  const [actionType, setActionType] = useState(1);

  // Asset Pair Dropdowns
  const [counterCurrency, setCounterCurrency] = useState([]);
  const [baseCurrency, setBaseCurrency] = useState([]);
  const [selectedCounter, setSelectedCounter] = useState("");
  const [selectedBase, setSelectedBase] = useState("");
  const [isDisabled, setIsDisabled] = useState(true);

  // Amounts & Prices (Matching Convert.js)
  const [onlyBuyAmount, setOnlyBuyAmount] = useState("");
  const [onlyBuyUSD, setOnlyBuyUSD] = useState("");
  const [onlyBuyPrice, setOnlyBuyPrice] = useState("");
  const [onlyBuyTotalPrice, setOnlyBuyTotalPrice] = useState("");
  const [onlyBuyPrice1, setOnlyBuyPrice1] = useState(0);

  const [onlySellAmount, setOnlySellAmount] = useState("");
  const [onlySellUSD, setOnlySellUSD] = useState("");
  const [onlySellPrice, setOnlySellPrice] = useState("");
  const [onlySellTotalPrice, setOnlySellTotalPrice] = useState("");
  const [onlySellPrice1, setOnlySellPrice1] = useState(0);

  const [swapBuy, setSwapBuy] = useState(false);
  const [swapSell, setSwapSell] = useState(false);

  // Balances
  const [balanceList, setBalanceList] = useState(() => {
    try {
      const cached = localStorage.getItem("user_balance_list");
      return cached ? JSON.parse(cached) : [];
    } catch (e) {
      return [];
    }
  });
  const [selectedBuyingAssetBalance, setSelectedBuyingAssetBalance] = useState(0);
  const [selectedSellingAssetBalance, setSelectedSellingAssetBalance] = useState(0);

  // Status & Eligibility
  const [isBuySellEligible, setIsBuySellEligible] = useState(false);
  const [marketPrice, setMarketPrice] = useState("");
  const [isBuyError, setIsBuyError] = useState(false);
  const [isBuyErrorDepth, setIsBuyErrorDepth] = useState(false);
  const [isSellError, setIsSellError] = useState(false);
  const [isSellErrorDepth, setIsSellErrorDepth] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [activePercent, setActivePercent] = useState(null);

  // Modals
  const [modalOpen, setModalOpen] = useState(false);
  const [showAccreditedPopup, setShowAccreditedPopup] = useState(false);
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [termsLinks, setTermsLinks] = useState([]);
  const [pendingProceed, setPendingProceed] = useState(null);
  const [pendingTxnType, setPendingTxnType] = useState(null);

  const getSpecificAssetPairPrecision = () => ({ pricePrecision: 4 });

  // 1. Initial Load: fetch currency list, balances, assets
  useEffect(() => {
    getBasecurrencyList();
    fetchBalances();
    fetchAssetsData();
  }, []);

  // Synchronize available balances whenever balanceList, selectedCounter, or selectedBase change
  useEffect(() => {
    if (!Array.isArray(balanceList) || balanceList.length === 0) return;

    if (selectedCounter) {
      const cItem = balanceList.find(
        (x) => x.currencyCode?.toUpperCase() === selectedCounter.toUpperCase()
      );
      setSelectedBuyingAssetBalance(cItem ? parseFloat(cItem.closingBalance || 0) : 0);
    } else {
      setSelectedBuyingAssetBalance(0);
    }

    if (selectedBase) {
      const bItem = balanceList.find(
        (x) => x.currencyCode?.toUpperCase() === selectedBase.toUpperCase()
      );
      setSelectedSellingAssetBalance(bItem ? parseFloat(bItem.closingBalance || 0) : 0);
    } else {
      setSelectedSellingAssetBalance(0);
    }
  }, [balanceList, selectedCounter, selectedBase]);

  // Listen to balance updates broadcast by other modules (e.g. BuyAndSell)
  useEffect(() => {
    const handleBalanceUpdate = (e) => {
      if (e.detail && Array.isArray(e.detail) && e.detail.length > 0) {
        setBalanceList(e.detail);
      }
    };
    window.addEventListener("spot_user_balance_update", handleBalanceUpdate);
    return () => {
      window.removeEventListener("spot_user_balance_update", handleBalanceUpdate);
    };
  }, []);

  // Re-fetch balances whenever pair or updateBalance triggers in Spot
  useEffect(() => {
    fetchBalances();
  }, [currencycode, basecurrency, updateBalance]);

  const prevContextPairRef = useRef({ currencycode: "", basecurrency: "" });

  // Sync initial pair and context pair changes from Spot dashboard
  useEffect(() => {
    if (
      currencycode &&
      (currencycode !== prevContextPairRef.current.currencycode ||
        basecurrency !== prevContextPairRef.current.basecurrency)
    ) {
      prevContextPairRef.current = { currencycode, basecurrency };
      const c = currencycode.toUpperCase();
      const b = basecurrency ? basecurrency.toUpperCase() : "";
      setSelectedCounter(c);
      if (buttonType === "Buy") {
        getCounterCurrency(c, b);
      } else {
        getCounterCurrencyForSell(c, b);
      }
    }
  }, [currencycode, basecurrency]);

  // Fetch all Counter currencies available for broker
  const getBasecurrencyList = async () => {
    try {
      const brokerId = localStorage.getItem("BROKERID");
      const res = await axios.get(`${WEBSERVICE}home/getAllCurrency/${brokerId}`, {
        headers: {
          "Content-Type": "application/json",
          authorization: `BEARER ${localStorage.getItem("access_token")}`,
        },
      });
      if (Array.isArray(res?.data?.currencyList)) {
        setCounterCurrency(res.data.currencyList);
      } else {
        fallbackCurrencies();
      }
    } catch (e) {
      fallbackCurrencies();
    }
  };

  const fallbackCurrencies = () => {
    try {
      const cached = JSON.parse(localStorage.getItem("spot_assets")) || [];
      const unique = [...new Set(cached.map((x) => x.currencyCode))];
      setCounterCurrency(unique);
    } catch (e) {}
  };

  const fetchAssetsData = async () => {
    try {
      const brokerId = localStorage.getItem("BROKERID");
      const response = await axios.get(
        `${CACHESERVICE}/getAssetsData?Name=Assets&BrokerId=${brokerId}`
      );
      const parsedCurrencyData = JSON.parse(response.data.value);
      if (parsedCurrencyData?.Values) {
        localStorage.setItem("spot_assets", JSON.stringify(parsedCurrencyData.Values));
      }
    } catch (error) {
      console.error("Error fetching assets data:", error);
    }
  };

  // Fetch live balances
  const fetchBalances = async () => {
    const userId = localStorage.getItem("userId") || localStorage.getItem("user_id");
    const uuid = localStorage.getItem("uuid");
    const token = localStorage.getItem("access_token");

    if (!userId || !token) return;

    try {
      const payload = { customerId: userId, uuid };
      const selectedMarginType = localStorage.getItem("selectedMarginType");
      if (selectedMarginType) {
        payload.marginType = selectedMarginType;
      }
      const res = await axios.post(
        `${WEBSERVICE}transaction/getUserBalance`,
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `BEARER ${token}`,
          },
        }
      );
      const list = res?.data?.userBalanceList || [];
      if (Array.isArray(list) && list.length > 0) {
        setBalanceList(list);
        try {
          localStorage.setItem("user_balance_list", JSON.stringify(list));
        } catch (e) {}
      }
    } catch (err) {
      console.error("Error fetching balances:", err);
    }
  };

  // Counter currency selected for Buy (matching Convert.js)
  const getCounterCurrency = (currency, preselectBase = "") => {
    reset();
    setSelectedCounter(currency);
    if (preselectBase) {
      setSelectedBase(preselectBase);
    }

    const brokerId = localStorage.getItem("BROKERID");
    const userCountry = localStorage.getItem("userCountry") || "";

    axios
      .get(
        `${WEBSERVICE}home/getBaseByCurrency?brokerId=${brokerId}&currency=${currency}&country=${userCountry}`,
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      )
      .then((response) => {
        const baseList = response?.data?.baseCurrencyList || [];
        setBaseCurrency(baseList);
        setIsDisabled(false);
        fetchBalances();

        const defaultBase = preselectBase && baseList.includes(preselectBase)
          ? preselectBase
          : baseList.length > 0
            ? baseList[0]
            : "";
        if (defaultBase) {
          getBaseCurrency(defaultBase, currency);
        }
      })
      .catch(() => {
        fallbackBaseCurrency(currency, preselectBase);
      });
  };

  // Counter currency selected for Sell (matching Convert.js)
  const getCounterCurrencyForSell = async (currency, preselectBase = "") => {
    reset();
    setSelectedCounter(currency);
    if (preselectBase) {
      setSelectedBase(preselectBase);
    }

    try {
      const brokerId = localStorage.getItem("BROKERID");
      const userCountry = localStorage.getItem("userCountry") || "";

      const response = await axios.get(
        `${WEBSERVICE}home/getBaseByCurrency?brokerId=${brokerId}&currency=${currency}&country=${userCountry}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      );
      const baseList = response?.data?.baseCurrencyList || [];
      setBaseCurrency(baseList);
      setIsDisabled(false);

      const defaultBase = preselectBase && baseList.includes(preselectBase)
        ? preselectBase
        : baseList.length > 0
          ? baseList[0]
          : "";
      if (defaultBase) {
        getBaseCurrency(defaultBase, currency);
      }
    } catch (e) {
      fallbackBaseCurrency(currency, preselectBase);
    }
  };

  const fallbackBaseCurrency = (currency, preselectBase = "") => {
    try {
      const cached = JSON.parse(localStorage.getItem("spot_assets")) || [];
      const matched = cached
        .filter((x) => x.currencyCode?.toUpperCase() === currency?.toUpperCase())
        .map((x) => x.baseCurrency)
        .filter(Boolean);
      const uniqueMatched = [...new Set(matched)];
      setBaseCurrency(uniqueMatched);
      setIsDisabled(false);
      const defaultBase = preselectBase && uniqueMatched.includes(preselectBase)
        ? preselectBase
        : uniqueMatched.length > 0
          ? uniqueMatched[0]
          : "";
      if (defaultBase) {
        getBaseCurrency(defaultBase, currency);
      }
    } catch (err) {}
  };

  // Base currency selected (matching Convert.js)
  const getBaseCurrency = (baseCurr, counterOverride = null) => {
    reset();
    const activeCounter = counterOverride || selectedCounter;
    setSelectedBase(baseCurr);
    if (activeCounter && baseCurr) {
      getPairWiseBuySellCheck(activeCounter, baseCurr, actionType);
    }
  };

  // Check pairwise eligibility & market price (matching Convert.js)
  const getPairWiseBuySellCheck = async (currency, base, action) => {
    if (!currency || !base) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const pairWiseResponse = await axios.get(
        `${WEBSERVICE}home/pairWiseBuySellChecking?currency=${currency}&baseCurrency=${base}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `BEARER ${token}`,
          },
        }
      );

      if (pairWiseResponse?.data?.error?.error_data !== 0) {
        showToast("error", pairWiseResponse?.data?.error?.error_msg || "Pair check failed");
        setIsLoading(false);
        return;
      }

      if (pairWiseResponse.data.buySellFlag === 0) {
        setIsBuySellEligible(false);
        const payload = { currency, baseCurrency: base, action };
        const marketPriceResponse = await axios.post(
          `${WEBSERVICE}home/marketPrice`,
          JSON.stringify(payload),
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `BEARER ${token}`,
            },
          }
        );

        if (marketPriceResponse?.data?.error?.error_data !== 0) {
          showToast("error", marketPriceResponse?.data?.error?.error_msg || "Error fetching price");
          setIsLoading(false);
          return;
        }

        const mp = parseFloat(marketPriceResponse.data.marketPrice);
        const precision = getSpecificAssetPairPrecision().pricePrecision;
        if (action === 1) {
          setOnlyBuyPrice(mp.toFixed(precision));
          setOnlyBuyAmount(1);
          setOnlyBuyTotalPrice((mp * 1).toFixed(precision));
          setMarketPrice(mp);
        } else {
          setOnlySellPrice(mp.toFixed(precision));
          setOnlySellAmount(1);
          setOnlySellTotalPrice((mp * 1).toFixed(precision));
        }
      } else {
        setIsBuySellEligible(true);
        // Pre-fetch price for 1 unit so user sees the rate immediately
        if (action === 1) {
          processBuyCalculation("1", currency, base);
        } else {
          processSellCalculation("1", currency, base);
        }
      }
    } catch (err) {
      console.error("Pairwise check error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Switching Buy and Sell tabs (matching Convert.js setActionAndTextType)
  const handleTabChange = (_, val) => {
    setActiveTab(val);
    const newAction = val === 0 ? 1 : 2;
    setActionType(newAction);
    if (val === 0) {
      setButtonType("Buy");
      setButtonTypeSmall("buy");
      setAccountType("Buying");
    } else {
      setButtonType("Sell");
      setButtonTypeSmall("sell");
      setAccountType("Selling");
    }
    reset();
    if (selectedCounter && selectedBase) {
      getPairWiseBuySellCheck(selectedCounter, selectedBase, newAction);
    }
  };

  const reset = () => {
    setOnlyBuyAmount("");
    setOnlyBuyUSD("");
    setOnlyBuyPrice("");
    setOnlyBuyTotalPrice("");
    setOnlySellAmount("");
    setOnlySellUSD("");
    setOnlySellPrice("");
    setOnlySellTotalPrice("");
    setIsBuyError(false);
    setIsBuyErrorDepth(false);
    setIsSellError(false);
    setIsSellErrorDepth(false);
    setActivePercent(null);
  };

  // USD Rate helper (matching Convert.js)
  const getUSDToCryptoRate = async (symbol) => {
    if (!symbol) return 0;
    try {
      const response = await axios.post(
        `${WEBSERVICE}home/marketPrice`,
        { currency: symbol, baseCurrency: "USD", action: 1 },
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      );
      return parseFloat(response?.data?.marketPrice) || 0;
    } catch (err) {
      return 0;
    }
  };

  // Buy Amount Changes (matching Convert.js)
  const handleBuyAmountChange = async (value) => {
    setOnlyBuyAmount(value);
    setIsSellError(false);
    setIsSellErrorDepth(false);
    setIsBuyError(false);
    setIsBuyErrorDepth(false);
    if (!value || parseFloat(value) <= 0) {
      setOnlyBuyUSD("");
      setOnlyBuyPrice("");
      setOnlyBuyTotalPrice("");
      return;
    }
    const rate = await getUSDToCryptoRate(selectedCounter);
    if (rate > 0) {
      setOnlyBuyUSD((parseFloat(value) * rate).toFixed(6));
    }
    processBuyCalculation(value);
  };

  const handleBuyUSDAmountChange = async (value) => {
    setOnlyBuyUSD(value);
    setIsSellError(false);
    setIsSellErrorDepth(false);
    setIsBuyError(false);
    setIsBuyErrorDepth(false);
    if (!value || parseFloat(value) <= 0) {
      setOnlyBuyAmount("");
      return;
    }
    const rate = await getUSDToCryptoRate(selectedCounter);
    if (rate > 0) {
      const cryptoAmount = (parseFloat(value) / rate).toFixed(8);
      setOnlyBuyAmount(cryptoAmount);
      processBuyCalculation(cryptoAmount);
    }
  };

  // Sell Amount Changes (matching Convert.js)
  const handleSellAmountChange = async (value) => {
    setOnlySellAmount(value);
    setIsSellError(false);
    setIsSellErrorDepth(false);
    setIsBuyError(false);
    setIsBuyErrorDepth(false);
    if (!value || parseFloat(value) <= 0) {
      setOnlySellUSD("");
      setOnlySellPrice("");
      setOnlySellTotalPrice("");
      return;
    }
    const rate = await getUSDToCryptoRate(selectedCounter);
    if (rate > 0) {
      setOnlySellUSD((parseFloat(value) * rate).toFixed(6));
    }
    processSellCalculation(value);
  };

  const handleSellUSDAmountChange = async (value) => {
    setOnlySellUSD(value);
    setIsSellError(false);
    setIsSellErrorDepth(false);
    setIsBuyError(false);
    setIsBuyErrorDepth(false);
    if (!value || parseFloat(value) <= 0) {
      setOnlySellAmount("");
      return;
    }
    const rate = await getUSDToCryptoRate(selectedCounter);
    if (rate > 0) {
      const cryptoAmount = (parseFloat(value) / rate).toFixed(8);
      setOnlySellAmount(cryptoAmount);
      processSellCalculation(cryptoAmount);
    }
  };

  // Calculations for Buy (matching Convert.js processBuyCalculation)
  const processBuyCalculation = (val, counterOverride = null, baseOverride = null) => {
    const counter = counterOverride || selectedCounter;
    const base = baseOverride || selectedBase;
    if (!counter || !base) return;

    if (isBuySellEligible) {
      axios
        .get(`${TRADESERVICE}?symbol=${counter}${base}&side=BID&amount=${val}`)
        .then((response) => {
          const result = response.data;
          if (result.statuscode != 0 && result.statuscode != "0" && result.price) {
            const price = parseFloat(result.price).toFixed(6);
            setOnlyBuyPrice(price);
            setOnlyBuyPrice1(parseFloat(result.price1 || result.price));
            const total = (parseFloat(price) * parseFloat(val)).toFixed(6);
            setOnlyBuyTotalPrice(total);
            setIsBuyError(parseFloat(total) < 0.001);
          } else {
            setOnlyBuyPrice(0);
            setOnlyBuyTotalPrice(0);
            setIsBuyErrorDepth(true);
          }
        })
        .catch(() => {
          fallbackMarketCalculation(val, 1, counter, base);
        });
    } else {
      fallbackMarketCalculation(val, 1, counter, base);
    }
  };

  // Calculations for Sell (matching Convert.js processSellCalculation)
  const processSellCalculation = (val, counterOverride = null, baseOverride = null) => {
    const counter = counterOverride || selectedCounter;
    const base = baseOverride || selectedBase;
    if (!counter || !base) return;

    if (isBuySellEligible) {
      axios
        .get(`${TRADESERVICE}?symbol=${counter}${base}&side=ASK&amount=${val}`)
        .then((response) => {
          const result = response.data;
          if (result.statuscode != 0 && result.statuscode != "0" && result.price) {
            const price = parseFloat(result.price).toFixed(6);
            setOnlySellPrice(price);
            setOnlySellPrice1(parseFloat(result.price1 || result.price));
            const total = (parseFloat(price) * parseFloat(val)).toFixed(6);
            setOnlySellTotalPrice(total);
            setIsSellError(parseFloat(total) < 0.001);
          } else {
            setOnlySellPrice(0);
            setOnlySellTotalPrice(0);
            setIsSellErrorDepth(true);
          }
        })
        .catch(() => {
          fallbackMarketCalculation(val, 2, counter, base);
        });
    } else {
      fallbackMarketCalculation(val, 2, counter, base);
    }
  };

  const fallbackMarketCalculation = (val, action, counterOverride = null, baseOverride = null) => {
    const counter = counterOverride || selectedCounter;
    const base = baseOverride || selectedBase;
    if (!counter || !base) return;

    axios
      .post(
        `${WEBSERVICE}home/marketPrice`,
        { currency: counter, baseCurrency: base, action },
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      )
      .then((response) => {
        if (response?.data?.error?.error_data !== 0) {
          if (response?.data?.error?.error_msg) {
            showToast("error", response.data.error.error_msg);
          }
        } else {
          const price = parseFloat(response.data.marketPrice).toFixed(6);
          const total = (parseFloat(price) * parseFloat(val)).toFixed(6);
          if (action === 1) {
            setOnlyBuyPrice(price);
            setOnlyBuyTotalPrice(total);
            setIsBuyError(parseFloat(total) < 0.001);
          } else {
            setOnlySellPrice(price);
            setOnlySellPrice1(parseFloat(price));
            setOnlySellTotalPrice(total);
            setIsSellError(parseFloat(total) < 0.001);
          }
        }
      })
      .catch(() => {});
  };

  // Quick Percentage Chips
  const handlePercentSelect = (pct) => {
    setActivePercent(pct);
    if (buttonType === "Buy") {
      const avail = parseFloat(selectedSellingAssetBalance || 0);
      if (avail <= 0) return;
      const targetBaseVal = (avail * (pct / 100));
      const curPrice = parseFloat(onlyBuyPrice) || parseFloat(marketPrice) || 1;
      const amt = (targetBaseVal / curPrice).toFixed(6);
      handleBuyAmountChange(amt);
    } else {
      const avail = parseFloat(selectedBuyingAssetBalance || 0);
      if (avail <= 0) return;
      const amt = (avail * (pct / 100)).toFixed(6);
      handleSellAmountChange(amt);
    }
  };

  // Re-fetch updated balance after order execution (matching Convert.js renderUpdatedBalance)
  const renderUpdatedBalance = (action) => {
    const userTransObj = {
      customerId: localStorage.getItem("user_id") || localStorage.getItem("userId"),
      uuid: localStorage.getItem("uuid"),
    };
    axios
      .post(`${WEBSERVICE}transaction/getUserBalance`, JSON.stringify(userTransObj), {
        headers: {
          "Content-Type": "application/json",
          authorization: `BEARER ${localStorage.getItem("access_token")}`,
        },
      })
      .then((response) => {
        const result = response.data;
        if (result?.userBalanceList) {
          const balance = result.userBalanceList;
          setBalanceList(balance);
          for (let i = 0; i < balance.length; i++) {
            if (action === "buy") {
              if (balance[i].currencyCode === selectedBase.toUpperCase()) {
                setSelectedSellingAssetBalance(balance[i].closingBalance);
                break;
              }
            } else {
              if (balance[i].currencyCode === selectedCounter.toUpperCase()) {
                setSelectedBuyingAssetBalance(balance[i].closingBalance);
                break;
              }
            }
          }
        }
      })
      .finally(() => setIsLoading(false));
  };

  // Market Buy Execution (matching Convert.js handleMarketBuy & doMarketBuy)
  const handleMarketBuy = async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      showToast("error", "Please login to trade");
      return;
    }

    setIsLoading(true);
    setIsButtonDisabled(true);

    const accreditationResult = await checkCurrencyPairAccreditedStatus(
      localStorage.getItem("userId") || localStorage.getItem("user_id"),
      selectedCounter,
      selectedBase,
      "BUY",
      onlyBuyAmount
    );

    if (accreditationResult && !accreditationResult.accessAllowed) {
      setIsLoading(false);
      setIsButtonDisabled(false);
      setShowAccreditedPopup(true);
      return;
    }

    const proceed = async () => {
      await doMarketBuy();
    };

    if (accreditationResult && accreditationResult.requiresTermsAgreement) {
      setIsLoading(false);
      setIsButtonDisabled(false);
      setTermsLinks(accreditationResult.links || []);
      setPendingProceed(() => proceed);
      setPendingTxnType(1);
      setShowTermsPopup(true);
      return;
    }

    await proceed();
  };

  const doMarketBuy = async () => {
    setIsLoading(true);
    setIsButtonDisabled(true);

    if (isBuySellEligible) {
      axios
        .get(`${TRADESERVICE}?symbol=${selectedCounter}${selectedBase}&side=BID&amount=${onlyBuyAmount}`)
        .then((response) => {
          const result = response.data;
          const priceVal = parseFloat(result.price).toFixed(6);
          const price1Val = parseFloat(result.price1 || result.price);
          setOnlyBuyPrice(priceVal);
          setOnlyBuyPrice1(price1Val);

          const inputObj = {
            selling_asset_code: selectedBase.toUpperCase(),
            buying_asset_code: selectedCounter.toUpperCase(),
            userId: localStorage.getItem("user_id") || localStorage.getItem("userId"),
            price: price1Val,
            txn_type: "1",
          };

          axios
            .post(WEBSERVICE + "/userTrade/OfferPriceCheck", inputObj, {
              headers: { "Content-Type": "application/json" },
            })
            .then((res) => {
              const checkResult = res.data;
              if (checkResult.error.error_data != 0) {
                showToast("error", checkResult.error.error_msg);
                setIsButtonDisabled(false);
                setIsLoading(false);
              } else {
                const assetCode = handleGetAssetCode(selectedCounter.toUpperCase(), selectedBase.toUpperCase());
                const assetId = handleGetAssetId(selectedCounter.toUpperCase(), selectedBase.toUpperCase()) || {};

                const tradeObj = {
                  selling_asset_code: selectedBase.toUpperCase(),
                  buying_asset_code: selectedCounter.toUpperCase(),
                  amount: parseFloat(onlyBuyAmount),
                  price: price1Val,
                  offerType: "M",
                  txn_type: "1",
                  assetCode: assetCode,
                  baseCurrencyId: assetId["baseCurrencyId"],
                  currencyId: assetId["currencyId"],
                  uuid: localStorage.getItem("uuid"),
                };

                axios
                  .post(WEBSERVICE + "userTrade/TradeCreateOffer", tradeObj, {
                    headers: {
                      "Content-Type": "application/json",
                      authorization: `BEARER ${localStorage.getItem("access_token")}`,
                    },
                  })
                  .then((data) => {
                    const tradeResult = data.data;
                    setIsButtonDisabled(false);
                    setIsLoading(false);
                    showToast(
                      tradeResult.error.error_data == 0 ? "success" : "error",
                      tradeResult.error.error_msg
                    );
                    reset();
                    renderUpdatedBalance("buy");
                    renderUpdatedBalance("sell");
                    if (setRefresh) setRefresh(!refresh);
                  });
              }
            })
            .catch(() => {
              setIsButtonDisabled(false);
              setIsLoading(false);
              showToast("error", "Error checking offer price");
            });
        })
        .catch(() => {
          setIsButtonDisabled(false);
          setIsLoading(false);
          showToast("error", "An error occurred while fetching trade data");
        });
    } else {
      const inputObj = {
        userId: localStorage.getItem("user_id") || localStorage.getItem("userId"),
        baseCurrency: selectedBase.toUpperCase(),
        currency: selectedCounter.toUpperCase(),
        amount: parseFloat(onlyBuyAmount),
        action: "1",
      };
      axios
        .post(WEBSERVICE + "/home/currencyConversion", inputObj, {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        })
        .then((response) => {
          setIsLoading(false);
          setIsButtonDisabled(false);
          showToast(
            response.data.error.error_data === 0 ? "success" : "error",
            response.data.error.error_msg
          );
          reset();
          renderUpdatedBalance("buy");
          if (setRefresh) setRefresh(!refresh);
        })
        .catch(() => {
          setIsLoading(false);
          setIsButtonDisabled(false);
          showToast("error", "An error occurred while converting currency");
        });
    }
  };

  // Market Sell Execution (matching Convert.js handleMarketSell & doMarketSell)
  const handleMarketSell = async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      showToast("error", "Please login to trade");
      return;
    }

    setIsLoading(true);
    setIsButtonDisabled(true);

    const accreditationResult = await checkCurrencyPairAccreditedStatus(
      localStorage.getItem("userId") || localStorage.getItem("user_id"),
      selectedCounter,
      selectedBase,
      "SELL",
      onlySellAmount
    );

    if (accreditationResult && !accreditationResult.accessAllowed) {
      setIsLoading(false);
      setIsButtonDisabled(false);
      setShowAccreditedPopup(true);
      return;
    }

    const proceed = async () => {
      await doMarketSell();
    };

    if (accreditationResult && accreditationResult.requiresTermsAgreement) {
      setIsLoading(false);
      setIsButtonDisabled(false);
      setTermsLinks(accreditationResult.links || []);
      setPendingProceed(() => proceed);
      setPendingTxnType(2);
      setShowTermsPopup(true);
      return;
    }

    await proceed();
  };

  const doMarketSell = async () => {
    setIsLoading(true);
    setIsButtonDisabled(true);

    if (isBuySellEligible) {
      axios
        .get(`${TRADESERVICE}?symbol=${selectedCounter}${selectedBase}&side=ASK&amount=${onlySellAmount}`)
        .then((response) => {
          const result = response.data;
          const setOnlySellPriceBak = response.data;
          const priceVal = parseFloat(result.price).toFixed(6);
          const price1Val = parseFloat(result.price1 || result.price);
          setOnlySellPrice(priceVal);
          setOnlySellPrice1(price1Val);

          const inputObj = {
            selling_asset_code: selectedCounter.toUpperCase(),
            buying_asset_code: selectedBase.toUpperCase(),
            userId: localStorage.getItem("user_id") || localStorage.getItem("userId"),
            price: price1Val,
            txn_type: "2",
          };

          axios
            .post(WEBSERVICE + "/userTrade/OfferPriceCheck", inputObj, {
              headers: { "Content-Type": "application/json" },
            })
            .then((res) => {
              const checkResult = res.data;
              if (checkResult.error.error_data != 0) {
                showToast("error", checkResult.error.error_msg);
                setIsLoading(false);
                setIsButtonDisabled(false);
              } else {
                const assetCode = handleGetAssetCode(selectedCounter.toUpperCase(), selectedBase.toUpperCase());
                const assetId = handleGetAssetId(selectedCounter.toUpperCase(), selectedBase.toUpperCase()) || {};

                const tradeObj = {
                  selling_asset_code: selectedCounter.toUpperCase(),
                  buying_asset_code: selectedBase.toUpperCase(),
                  amount: parseFloat(onlySellAmount),
                  price: parseFloat(setOnlySellPriceBak.price1 || setOnlySellPriceBak.price),
                  offerType: "M",
                  txn_type: "2",
                  assetCode: assetCode,
                  baseCurrencyId: assetId["baseCurrencyId"],
                  currencyId: assetId["currencyId"],
                  uuid: localStorage.getItem("uuid"),
                };

                axios
                  .post(WEBSERVICE + "/userTrade/TradeCreateOffer", tradeObj, {
                    headers: {
                      "Content-Type": "application/json",
                      authorization: `BEARER ${localStorage.getItem("access_token")}`,
                    },
                  })
                  .then((data) => {
                    setIsLoading(false);
                    setIsButtonDisabled(false);
                    const tradeResult = data.data;
                    showToast(
                      tradeResult.error.error_data == 0 ? "success" : "error",
                      tradeResult.error.error_msg
                    );
                    reset();
                    renderUpdatedBalance("buy");
                    renderUpdatedBalance("sell");
                    if (setRefresh) setRefresh(!refresh);
                  });
              }
            })
            .catch(() => {
              setIsLoading(false);
              setIsButtonDisabled(false);
              showToast("error", "Error checking offer price");
            });
        })
        .catch(() => {
          setIsLoading(false);
          setIsButtonDisabled(false);
          showToast("error", "An error occurred while fetching trade data");
        });
    } else {
      const inputObj = {
        userId: localStorage.getItem("user_id") || localStorage.getItem("userId"),
        baseCurrency: selectedBase.toUpperCase(),
        currency: selectedCounter.toUpperCase(),
        amount: parseFloat(onlySellAmount),
        action: "2",
      };
      axios
        .post(WEBSERVICE + "/home/currencyConversion", inputObj, {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        })
        .then((data) => {
          setIsLoading(false);
          setIsButtonDisabled(false);
          const convResult = data.data;
          showToast(
            convResult.error.error_data === 0 ? "success" : "error",
            convResult.error.error_msg
          );
          reset();
          renderUpdatedBalance("sell");
          if (setRefresh) setRefresh(!refresh);
        })
        .catch(() => {
          setIsLoading(false);
          setIsButtonDisabled(false);
          showToast("error", "An error occurred while converting currency");
        });
    }
  };

  const isBuy = buttonType === "Buy";
  const actionColor = isBuy ? (upColor || "#16a34a") : (downColor || "#ef4444");

  const currentAvailableBalance = isBuy ? selectedSellingAssetBalance : selectedBuyingAssetBalance;
  const balanceAssetCode = isBuy ? selectedBase : selectedCounter;

  const currentAmount = isBuy
    ? !swapBuy ? onlyBuyAmount : onlyBuyUSD
    : !swapSell ? onlySellAmount : onlySellUSD;

  const currentPrice = isBuy ? onlyBuyPrice : onlySellPrice;
  const currentTotalPrice = isBuy ? onlyBuyTotalPrice : onlySellTotalPrice;

  const isActionDisabled =
    isBuy
      ? !(parseFloat(onlyBuyAmount) > 0) || isBuyError || isBuyErrorDepth || isLoading || isButtonDisabled
      : !(parseFloat(onlySellAmount) > 0) || isSellError || isSellErrorDepth || isLoading || isButtonDisabled;

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "var(--body-color-less)",
        color: "var(--text-color)",
        fontSize: "12px",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {/* ── Tab Bar (Matching BuyAndSell bs-tabbar) ── */}
      <div className="bs-tabbar" style={{ justifyContent: "space-between", paddingRight: "10px" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <button
            type="button"
            className={`bs-tab ${activeTab === 0 ? "active-bs-tab" : ""}`}
            style={activeTab === 0 ? { "--btn-color": upColor || "#16a34a", color: upColor || "#16a34a", opacity: 1 } : {}}
            onClick={() => handleTabChange(null, 0)}
          >
            BUY CONVERT
          </button>
          <button
            type="button"
            className={`bs-tab ${activeTab === 1 ? "active-bs-tab" : ""}`}
            style={activeTab === 1 ? { "--btn-color": downColor || "#ef4444", color: downColor || "#ef4444", opacity: 1 } : {}}
            onClick={() => handleTabChange(null, 1)}
          >
            SELL CONVERT
          </button>
        </div>

        {/* View Fees link and balance refresh */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span
            onClick={() => setModalOpen(true)}
            style={{
              fontSize: "11px",
              fontWeight: 600,
              color: "var(--btn-color, #F0B90B)",
              cursor: "pointer",
            }}
          >
            Fees
          </span>
          <Tooltip title="Refresh balances">
            <span
              onClick={fetchBalances}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                opacity: 0.6,
                color: "var(--text-color)",
              }}
            >
              <RefreshIcon style={{ fontSize: 14 }} />
            </span>
          </Tooltip>
        </div>
      </div>

      {/* ── Trade Form Panel (Matching BuySellMarket trade-panel) ── */}
      <div
        className="trade-panel"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          overflowY: "auto",
          padding: "10px 10px 12px",
          minHeight: 0,
        }}
      >
        {/* ── Currency Pair Row: Counter Currency & Base Currency ── */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {/* Counter Currency Dropdown */}
          <div className="trade-input-group" style={{ flex: 1, marginBottom: 0 }}>
            <label>{buttonType === "Buy" ? "Buy Asset" : "Sell Asset"}</label>
            <div className="trade-input-box" style={{ height: "30px" }}>
              <select
                value={selectedCounter}
                onChange={(e) =>
                  buttonType === "Buy"
                    ? getCounterCurrency(e.target.value)
                    : getCounterCurrencyForSell(e.target.value)
                }
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  color: "var(--text-color)",
                  fontSize: "12px",
                  fontWeight: 600,
                  padding: "4px 8px",
                  cursor: "pointer",
                  width: "100%",
                }}
              >
                <option value="" style={{ background: "var(--body-color)", color: "var(--text-color)" }}>Select</option>
                {counterCurrency.map((item, idx) => (
                  <option key={idx} value={item} style={{ background: "var(--body-color)", color: "var(--text-color)" }}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <SyncAltOutlinedIcon style={{ fontSize: 16, opacity: 0.45, color: "var(--text-color)", marginTop: 14 }} />

          {/* Base Currency Dropdown */}
          <div className="trade-input-group" style={{ flex: 1, marginBottom: 0 }}>
            <label>{buttonType === "Buy" ? "With Asset" : "For Asset"}</label>
            <div
              className="trade-input-box"
              style={{
                height: "30px",
                opacity: isDisabled || baseCurrency.length === 0 ? 0.6 : 1,
              }}
            >
              <select
                value={selectedBase}
                disabled={isDisabled || baseCurrency.length === 0}
                onChange={(e) => getBaseCurrency(e.target.value)}
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  color: "var(--text-color)",
                  fontSize: "12px",
                  fontWeight: 600,
                  padding: "4px 8px",
                  cursor: isDisabled || baseCurrency.length === 0 ? "not-allowed" : "pointer",
                  width: "100%",
                }}
              >
                <option value="" style={{ background: "var(--body-color)", color: "var(--text-color)" }}>Select</option>
                {baseCurrency.map((item, idx) => (
                  <option key={idx} value={item} style={{ background: "var(--body-color)", color: "var(--text-color)" }}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* ── Balance Strip (Matching BuySellMarket trade-bal-row) ── */}
        <div className="trade-bal-row" style={{ marginTop: "2px", marginBottom: "4px" }}>
          <span className="bal-amount">
            Available:{" "}
            <span style={{ fontWeight: 700, color: "var(--text-color)" }}>
              {Number(currentAvailableBalance || 0).toFixed(4)} {balanceAssetCode}
            </span>
          </span>
          {balanceAssetCode && (
            <span
              onClick={() => handlePercentSelect(100)}
              style={{
                fontSize: "10.5px",
                fontWeight: 700,
                cursor: "pointer",
                color: isBuy ? (upColor || "#16a34a") : (downColor || "#ef4444"),
              }}
            >
              MAX
            </span>
          )}
        </div>

        {/* ── Amount Input (Buying Amount / Selling Amount) ── */}
        <div className="trade-input-group" style={{ marginBottom: "6px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "3px" }}>
            <label style={{ margin: 0 }}>{buttonType === "Buy" ? "Buying Amount" : "Selling Amount"}</label>

            {/* Swap toggle USD vs Crypto */}
            {((buttonType === "Buy" && selectedCounter && onlyBuyAmount) ||
              (buttonType === "Sell" && selectedCounter && onlySellAmount)) && (
              <span
                onClick={buttonType === "Buy" ? () => setSwapBuy((p) => !p) : () => setSwapSell((p) => !p)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "2px",
                  cursor: "pointer",
                  fontSize: "9.5px",
                  fontWeight: 600,
                  color: isBuy ? (upColor || "#16a34a") : (downColor || "#ef4444"),
                }}
              >
                <SwapVertIcon style={{ fontSize: 13 }} />
                <span>
                  {buttonType === "Buy"
                    ? `${swapBuy ? selectedCounter : "USD"}: ${swapBuy ? onlyBuyAmount : onlyBuyUSD}`
                    : `${swapSell ? selectedCounter : "USD"}: ${swapSell ? onlySellAmount : onlySellUSD}`}
                </span>
              </span>
            )}
          </div>

          <div className="trade-input-box">
            <input
              type="text"
              placeholder="0.00"
              disabled={selectedBase === ""}
              value={currentAmount}
              onChange={(e) => {
                if (buttonType === "Buy") {
                  !swapBuy ? handleBuyAmountChange(e.target.value) : handleBuyUSDAmountChange(e.target.value);
                } else {
                  !swapSell ? handleSellAmountChange(e.target.value) : handleSellUSDAmountChange(e.target.value);
                }
              }}
            />
            <span className="trade-unit">
              {buttonType === "Buy"
                ? !swapBuy ? selectedCounter : "USD"
                : !swapSell ? selectedCounter : "USD"}
            </span>
          </div>
        </div>

        {/* ── Percentage Quick Buttons (Matching BuySellMarket trade-pct-row) ── */}
        <div className="trade-pct-row" style={{ marginTop: "4px", marginBottom: "6px" }}>
          {[25, 50, 75, 100].map((pct) => (
            <button
              key={pct}
              type="button"
              className={`trade-pct-btn ${
                activePercent === pct
                  ? isBuy
                    ? "pct-active-buy"
                    : "pct-active-sell"
                  : ""
              }`}
              onClick={() => handlePercentSelect(pct)}
            >
              {pct}%
            </button>
          ))}
        </div>

        {/* ── Price & Total Price Fields (Matching BuySellMarket) ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px", marginBottom: "6px" }}>
          {/* Price */}
          <div className="trade-input-group" style={{ marginBottom: 0 }}>
            <label>Price</label>
            <div className="trade-input-box">
              <input
                type="text"
                value={currentPrice || "0.00"}
                readOnly
              />
              <span className="trade-unit">{selectedBase || "--"}</span>
            </div>
          </div>

          {/* Total Price */}
          <div className="trade-input-group" style={{ marginBottom: 0 }}>
            <label>Total Price</label>
            <div className="trade-input-box">
              <input
                type="text"
                value={currentTotalPrice || "0.00"}
                readOnly
              />
              <span className="trade-unit">{selectedBase || "--"}</span>
            </div>
          </div>
        </div>

        {/* ── Validation Error Messages ── */}
        {(buttonType === "Buy" ? isBuyErrorDepth : isSellErrorDepth) && (
          <div style={{ fontSize: "11px", color: "var(--trading-down-color, #ef4444)", fontWeight: 500 }}>
            Orderbook depth reached — price not found
          </div>
        )}
        {(buttonType === "Buy" ? isBuyError : isSellError) && (
          <div style={{ fontSize: "11px", color: "var(--trading-down-color, #ef4444)", fontWeight: 500 }}>
            Total price must be at least 0.001
          </div>
        )}

        {/* ── Action Button (Matching BuySellMarket trade-action-btn) ── */}
        <div style={{ marginTop: "auto", paddingTop: "4px" }}>
          <button
            type="button"
            className={`trade-action-btn ${isBuy ? "btn-buy" : "btn-sell"}`}
            disabled={isActionDisabled}
            onClick={buttonType === "Buy" ? handleMarketBuy : handleMarketSell}
          >
            {isLoading ? (
              <CircularProgress size={16} sx={{ color: "#ffffff" }} />
            ) : (
              <span>
                {buttonType === "Buy"
                  ? `BUY ${selectedCounter || ""}`
                  : `SELL ${selectedCounter || ""}`}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Fee Modal (Matching Convert.js) */}
      <FeeModal open={modalOpen} onClose={() => setModalOpen(false)} />

      {/* KYC / Accreditation Popups (Matching Convert.js) */}
      <AccreditationVerificationPopup
        open={showAccreditedPopup}
        onClose={() => setShowAccreditedPopup(false)}
        theme={theme}
      />
      <TermsAgreementPopup
        open={showTermsPopup}
        onClose={() => {
          setShowTermsPopup(false);
          setPendingProceed(null);
        }}
        onProceed={() => {
          if (typeof pendingProceed === "function") {
            pendingProceed();
          }
          setPendingProceed(null);
        }}
        links={termsLinks}
        theme={theme}
        vestingCurrency={selectedCounter}
        txnType={pendingTxnType}
      />
    </div>
  );
};

export default QuickSwap;
